import { Routes, Route, Navigate, useLocation } from "react-router-dom"
import { Loader2 } from "lucide-react"
import { useAuth } from "@/hooks/useAuth"
import LoginPage from "@/pages/LoginPage"
import ForgotPasswordPage from "@/pages/ForgotPasswordPage"
import DashboardPage from "@/pages/DashboardPage"
import ComingSoonPage from "@/pages/ComingSoonPage"
import ContactsListPage from "@/pages/contacts/ContactsListPage"
import LeadsPage from "@/pages/leads/LeadsPage"
import NewContactPage from "@/pages/contacts/NewContactPage"
import ContactDetailPage from "@/pages/contacts/ContactDetailPage"
import PipelinePage from "@/pages/deals/PipelinePage"
import DealsListPage from "@/pages/deals/DealsListPage"
import NewDealPage from "@/pages/deals/NewDealPage"
import DealDetailPage from "@/pages/deals/DealDetailPage"
import TasksPage from "@/pages/TasksPage"
import DocumentsPage from "@/pages/DocumentsPage"
import TemplatesPage from "@/pages/TemplatesPage"
import ReportsPage from "@/pages/ReportsPage"
import SettingsPage from "@/pages/SettingsPage"
import UsersPage from "@/pages/UsersPage"
import MySettingsPage from "@/pages/MySettingsPage"
import AuditLogPage from "@/pages/AuditLogPage"
import LendersPage from "@/pages/LendersPage"
import AmbitionPage from "@/pages/AmbitionPage"
import UserManualPage from "@/pages/UserManualPage"
import ImportPage from "@/pages/ImportPage"
import AutomationsPage from "@/pages/AutomationsPage"
import AutomationDetailPage from "@/pages/automations/AutomationDetailPage"

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth()
  const location = useLocation()

  if (isLoading) {
    return (
      <div className="text-muted-foreground grid min-h-screen place-items-center">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    )
  }
  if (!user) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />
  }
  return <>{children}</>
}

// All routes now have real pages; ComingSoonPage kept for future stubs.
void ComingSoonPage

export default function App() {
  return (
    <Routes>
      {/* Public */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />

      {/* Authenticated */}
      <Route
        path="/"
        element={
          <RequireAuth>
            <DashboardPage />
          </RequireAuth>
        }
      />

      {/* Phase 2 */}
      <Route
        path="/contacts"
        element={
          <RequireAuth>
            <ContactsListPage />
          </RequireAuth>
        }
      />
      <Route
        path="/contacts/new"
        element={
          <RequireAuth>
            <NewContactPage />
          </RequireAuth>
        }
      />
      <Route
        path="/contacts/:id"
        element={
          <RequireAuth>
            <ContactDetailPage />
          </RequireAuth>
        }
      />

      {/* Phase 3 */}
      <Route
        path="/pipeline"
        element={
          <RequireAuth>
            <PipelinePage />
          </RequireAuth>
        }
      />
      <Route
        path="/deals"
        element={
          <RequireAuth>
            <DealsListPage />
          </RequireAuth>
        }
      />
      <Route
        path="/leads"
        element={
          <RequireAuth>
            <LeadsPage />
          </RequireAuth>
        }
      />
      <Route
        path="/deals/new"
        element={
          <RequireAuth>
            <NewDealPage />
          </RequireAuth>
        }
      />
      <Route
        path="/deals/:id"
        element={
          <RequireAuth>
            <DealDetailPage />
          </RequireAuth>
        }
      />

      {/* Phase 4 */}
      <Route
        path="/tasks"
        element={
          <RequireAuth>
            <TasksPage />
          </RequireAuth>
        }
      />
      <Route
        path="/reports"
        element={
          <RequireAuth>
            <ReportsPage />
          </RequireAuth>
        }
      />



      {/* Phase 6 */}
      <Route
        path="/documents"
        element={
          <RequireAuth>
            <DocumentsPage />
          </RequireAuth>
        }
      />
      <Route
        path="/templates"
        element={
          <RequireAuth>
            <TemplatesPage />
          </RequireAuth>
        }
      />
      <Route path="/import" element={<RequireAuth><ImportPage /></RequireAuth>} />

      {/* Phase 7 */}
      <Route
        path="/automations"
        element={
          <RequireAuth>
            <AutomationsPage />
          </RequireAuth>
        }
      />
      <Route
        path="/automations/:id"
        element={
          <RequireAuth>
            <AutomationDetailPage />
          </RequireAuth>
        }
      />

      {/* Phase 8 */}
      <Route path="/lenders" element={<RequireAuth><LendersPage /></RequireAuth>} />
      <Route path="/ambition" element={<RequireAuth><AmbitionPage /></RequireAuth>} />
      <Route path="/manual" element={<RequireAuth><UserManualPage /></RequireAuth>} />
      <Route
        path="/my-settings"
        element={
          <RequireAuth>
            <MySettingsPage />
          </RequireAuth>
        }
      />
      <Route
        path="/settings"
        element={
          <RequireAuth>
            <SettingsPage />
          </RequireAuth>
        }
      />
      <Route
        path="/users"
        element={
          <RequireAuth>
            <UsersPage />
          </RequireAuth>
        }
      />
      <Route path="/audit" element={<RequireAuth><AuditLogPage /></RequireAuth>} />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}
